import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  Tooltip,
  Typography,
  Divider,
  TableContainer,
  Grid,
} from "@mui/material";

import { Edit, CheckCircle, Cancel, Add } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import { CandidateSkillAction } from "../../../Redux/action/CandidateSkillAction";
import { AddSkillsAction } from "../../../Redux/action/AddSkillsAction";
import {
  CandidateSkillUpdateAction,
  DeleteSkills,
} from "../../../Redux/action/CandidateSkillUpdateAction";
import { useNavigate } from "react-router";

const PRIMARY_COLOR = "#286a94";

const Skills = ({ setActiveTab }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userResult } = useSelector((state) => state.Auth);
  const userInfo = useSelector((state) => state?.CandidateSkills?.result);
  const candidateId = userResult?.userid;

  const [skillsList, setSkillsList] = useState([]);
  const [showNewSkillFields, setShowNewSkillFields] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [newSkill, setNewSkill] = useState({
    skills: "",
    experience: "",
    rating: "",
  });

  const [editedSkill, setEditedSkill] = useState({
    skills: "",
    experience: "",
    durationValue: "",
    durationUnit: "",
    rating: "",
    index: -1,
  });

  useEffect(() => {
    dispatch(CandidateSkillAction(candidateId));
  }, [dispatch]);

  useEffect(() => {
    if (userInfo) {
      setSkillsList(
        userInfo.map((skill) => ({
          candidateSkillsId: skill.candidateSkillsId,
          skills: skill.skills ?? "NA",
          experience: skill.experience ?? "NA",
          durationValue: skill.experience ? parseInt(skill.experience) : "NA",
          durationUnit: skill.experience
            ? skill.experience.includes("Years")
              ? "Years"
              : "Months"
            : "",
          rating: skill.rating ?? "NA",
        }))
      );
    }
  }, [userInfo]);

  const handleEditSkill = (index) => {
    const skillToEdit = skillsList[index];
    setEditedSkill({
      skills: skillToEdit.skills,
      experience: skillToEdit.experience,
      durationValue: skillToEdit.durationValue,
      durationUnit: skillToEdit.durationUnit,
      rating: skillToEdit.rating,
      index,
    });
    setEditMode(true);
  };

  const handleAddNewSkill = () => {
    if (!newSkill.skills || !newSkill.experience || !newSkill.rating) {
      toast.error("All fields required");
      return;
    }

    const content = {
      candidateId,
      skills: newSkill.skills,
      experience: newSkill.experience,
      rating: newSkill.rating,
    };

    dispatch(AddSkillsAction(content));
    setShowNewSkillFields(false);
    setNewSkill({ skills: "", experience: "", rating: "" });

    setTimeout(() => dispatch(CandidateSkillAction(candidateId)), 800);
  };

  const handleSaveEdit = async (candidateSkillsId) => {
    const { skills, durationValue, durationUnit, rating } = editedSkill;
    if (!durationValue || !durationUnit) {
      toast.error("Please enter your experience");
      return;
    }

    const content = {
      candidateSkillsId,
      skills,
      experience: `${durationValue} ${durationUnit}`,
      rating,
    };

    dispatch(CandidateSkillUpdateAction(content));
    setEditMode(false);

    setTimeout(() => dispatch(CandidateSkillAction(candidateId)), 1000);
  };

  const handleDeleteSkill = (skillId) => {
    dispatch(DeleteSkills(skillId));
    setTimeout(() => dispatch(CandidateSkillAction(candidateId)), 500);
  };

  return (
  <>
  <Box sx={{ width: "100%", mt: 1 }}>
    <Typography variant="h6" sx={{ fontWeight: 600, color: PRIMARY_COLOR }}>
      Skills & Ratings
    </Typography>

    <Divider sx={{ mb: 2, mt: 1 }} />

    {/* ADD NEW BUTTON */}
    <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
      <Button
        variant="contained"
        sx={{ bgcolor: PRIMARY_COLOR }}
        onClick={() => setShowNewSkillFields(!showNewSkillFields)}
        startIcon={<Add />}
      
      >
        {showNewSkillFields ? "Close" : "Add Skill"}
      </Button>
    </Box>

    {/* ADD FORM */}
    {showNewSkillFields && (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Skill Name"
              value={newSkill.skills}
              onChange={(e) =>
                setNewSkill({ ...newSkill, skills: e.target.value })
              }
              fullWidth
            />
          </Grid>

          <Grid item xs={6} sm={4}>
            <TextField
              label="Experience"
              value={newSkill.durationValue}
              type="number"
              onChange={(e) =>
                setNewSkill({ ...newSkill, durationValue: e.target.value })
              }
              fullWidth
            />
          </Grid>

          <Grid item xs={6} sm={4}>
            <Select
              value={newSkill.durationUnit}
              onChange={(e) =>
                setNewSkill({ ...newSkill, durationUnit: e.target.value })
              }
              fullWidth
              displayEmpty
            >
              <MenuItem value="">Select Unit</MenuItem>
              <MenuItem value="Months">Months</MenuItem>
              <MenuItem value="Years">Years</MenuItem>
            </Select>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Select
              value={newSkill.rating}
              onChange={(e) =>
                setNewSkill({ ...newSkill, rating: e.target.value })
              }
              fullWidth
              displayEmpty
            >
              <MenuItem value="">Rating</MenuItem>
              <MenuItem value="Excellent">Excellent</MenuItem>
              <MenuItem value="Good">Good</MenuItem>
              <MenuItem value="Average">Average</MenuItem>
            </Select>
          </Grid>

          <Grid item xs={12} sm={2}>
            <IconButton
              color="success"
              onClick={handleAddNewSkill}
              sx={{ mt: { xs: 1, sm: "50%" } }}
            >
              <CheckCircle />
            </IconButton>
          </Grid>
        </Grid>
      </Paper>
    )}

    {/* TABLE LIST */}
    <TableContainer
      component={Paper}
      sx={{ maxHeight: 330, overflow: "auto", width: "100%" }}
    >
      <Table stickyHeader sx={{ minWidth: { xs: "600px", sm: "auto" } }}>
        <TableHead>
          <TableRow>
            <TableCell><b>S.No</b></TableCell>
            <TableCell><b>Skills</b></TableCell>
            <TableCell><b>Experience</b></TableCell>
            <TableCell><b>Rating</b></TableCell>
            <TableCell><b>Actions</b></TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {skillsList.length ? (
            skillsList.map((skill, index) => (
              <TableRow key={index}>
                <TableCell>{index + 1}</TableCell>

                {editMode && editedSkill.index === index ? (
                  <>
                    <TableCell>
                      <TextField value={editedSkill.skills} disabled fullWidth />
                    </TableCell>

                    <TableCell>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <TextField
                            type="number"
                            value={editedSkill.durationValue}
                            onChange={(e) =>
                              setEditedSkill({
                                ...editedSkill,
                                durationValue: e.target.value,
                              })
                            }
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Select
                            value={editedSkill.durationUnit}
                            onChange={(e) =>
                              setEditedSkill({
                                ...editedSkill,
                                durationUnit: e.target.value,
                              })
                            }
                            fullWidth
                          >
                            <MenuItem value="Months">Months</MenuItem>
                            <MenuItem value="Years">Years</MenuItem>
                          </Select>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell>
                      <Select
                        value={editedSkill.rating}
                        onChange={(e) =>
                          setEditedSkill({
                            ...editedSkill,
                            rating: e.target.value,
                          })
                        }
                        fullWidth
                      >
                        <MenuItem value="Excellent">Excellent</MenuItem>
                        <MenuItem value="Good">Good</MenuItem>
                        <MenuItem value="Average">Average</MenuItem>
                      </Select>
                    </TableCell>

                    <TableCell>
                      <IconButton
                        color="success"
                        onClick={() => handleSaveEdit(skill.candidateSkillsId)}
                      >
                        <CheckCircle />
                      </IconButton>
                      <IconButton color="error" onClick={() => setEditMode(false)}>
                        <Cancel />
                      </IconButton>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>{skill.skills}</TableCell>
                    <TableCell>{`${skill.durationValue} ${skill.durationUnit}`}</TableCell>
                    <TableCell>{skill.rating}</TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton
                          sx={{ color: PRIMARY_COLOR }}
                          onClick={() => handleEditSkill(index)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} align="center">
                No Skills Added
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>

    <Button variant="outlined" sx={{ mt: 3 }} onClick={() => setActiveTab("Profile")}>
      Back
    </Button>
  </Box>
</>

  );
};

export default Skills;
